import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SamplePreview } from "@/components/SamplePreview";
import { SampleCard } from "@/components/SampleCard";
import { MaskLines, Reveal } from "@/components/Motion";
import { api, SUBJECTS } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function Samples() {
  const { t, lang, isRtl } = useLang();
  const [items, setItems] = useState(null);
  const [subject, setSubject] = useState("all");
  const [open, setOpen] = useState(null);
  const Arrow = isRtl ? ArrowLeft : ArrowRight;

  useEffect(() => {
    api.get("/samples").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  const shown = (items || []).filter((s) => subject === "all" || s.subject === subject);

  return (
    <div data-testid="samples-page">
      <Navbar />
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-14 pb-24 min-h-[70vh]">
        <p className="text-sm font-semibold text-[#B45309] mb-3">{t.samples.eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"><MaskLines key={lang} lines={[t.samples.title]} delay={0.1} /></h1>
        <p className="text-stone-700 max-w-2xl">{t.samples.sub}</p>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="samples-filters">
          {["all", ...SUBJECTS.map((s) => s.id)].map((id) => (
            <button key={id} onClick={() => setSubject(id)} data-testid={`samples-filter-${id}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${subject === id ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]" : "border-[#E5DCCB] bg-white hover:border-[#1C1917]"}`}>
              {id === "all" ? t.samples.all : t.subjects[id].name}
            </button>
          ))}
        </div>

        <div className="mt-10 space-y-12" data-testid="samples-grid">
          {SUBJECTS.filter((x) => subject === "all" || x.id === subject).map((subj) => (
            <Reveal key={subj.id}>
              <section data-testid={`samples-lang-${subj.id}`}>
                <h2 className="font-display text-3xl font-bold mb-4 flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full" style={{ background: subj.tone }} /> {t.subjects[subj.id].name}
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8">
                  {[1, 2, 3, 4].map((lvl) => {
                    const s = shown.find((x) => x.subject === subj.id && x.level === lvl);
                    return s ? (
                      <SampleCard key={lvl} s={s} onOpen={(item, listen) => setOpen({ item, listen })} />
                    ) : (
                      <div key={lvl} className="flex flex-col justify-between border border-dashed border-[#D6CBB5] bg-[#FAF7F2] rounded-sm p-5 aspect-[4/3]" data-testid={`sample-empty-${subj.id}-${lvl}`}>
                        <span className="self-start text-xs font-bold px-2.5 py-1 rounded-full bg-[#F2ECE1]">{t.lib.level} {lvl} · {t.levels[lvl]}</span>
                        <p className="text-sm text-stone-600">{t.samples.soon}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Link to="/register" data-testid="samples-register-btn" className="inline-flex items-center gap-2 rounded-full bg-[#8B3A2B] text-white px-7 py-4 font-semibold hover:bg-[#702E22] transition-colors">
            {t.samples.full} <Arrow className="w-5 h-5" />
          </Link>
        </div>
      </section>
      <Footer />
      {open && <SamplePreview sample={open.item} initialListen={open.listen} onClose={() => setOpen(null)} />}
    </div>
  );
}
